import { useCallback } from 'react';
import {
  V2BookConnectionState,
  type V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import { retrieveWorkspaceBookConnection } from '@/generated/hooks/bdk/v2/services/library';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';

/** Default polling interval in milliseconds (3 seconds) */
const DEFAULT_POLLING_INTERVAL = 3000;

/** Default max retries before giving up (100 retries = ~5 minutes with 3s interval) */
const DEFAULT_MAX_RETRIES = 100;

/** Terminal states that indicate polling should stop */
const TERMINAL_STATES: V2BookConnectionState[] = [
  V2BookConnectionState.BOOK_CONNECTION_STATE_READY,
  V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED,
  V2BookConnectionState.BOOK_CONNECTION_STATE_CONDITIONS_NOT_MET,
  // A connection scaled to zero for inactivity has settled — it will not move
  // to READY on its own, so polling on would just burn the retry budget until
  // it times out. The caller reactivates it explicitly instead.
  V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE,
];

/**
 * Result returned when connection reaches terminal state, times out, or is cancelled.
 */
export interface IWaitForConnectionResult {
  /** Final connection state */
  state: V2BookConnectionState;
  /** True when state is READY */
  isReady: boolean;
  /** True when state is FAILED */
  isFailed: boolean;
  /** True when state is CONDITIONS_NOT_MET */
  isConditionsNotMet: boolean;
  /** True when state is INACTIVE (scaled to zero, needs an explicit reactivate) */
  isInactive: boolean;
  /** True when polling timed out before reaching a terminal state */
  isTimedOut: boolean;
  /** True when polling was cancelled via AbortSignal */
  isCancelled: boolean;
  /** Full connection descriptor */
  connection: V2BookConnectionDescriptor | undefined;
}

/**
 * Checks if the given state is a terminal state.
 *
 * @param state - The connection state to check
 * @returns True if the state is terminal (ready, failed, conditions not met, or inactive)
 */
const isTerminalState = (state: V2BookConnectionState | undefined): boolean => {
  if (!state) return false;
  return TERMINAL_STATES.includes(state);
};

/**
 * Delays execution for the specified duration.
 *
 * @param ms - Duration to wait in milliseconds
 * @returns Promise that resolves after the delay
 */
const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Options for the useWaitForConnectionToBeReady hook.
 */
export interface IUseWaitForConnectionToBeReadyOptions {
  /** Polling interval in milliseconds. Defaults to 3000ms. */
  pollingInterval?: number;
  /** Maximum number of retries before giving up. Defaults to 100 (~5 minutes with 3s interval). */
  maxRetries?: number;
}

/**
 * Hook that returns a function to poll a connection until it reaches a terminal state.
 *
 * The returned function will poll the connection API every 3 seconds (configurable)
 * until the connection state is no longer PENDING or UNKNOWN. Terminal states are:
 * - READY
 * - FAILED
 * - CONDITIONS_NOT_MET
 * - INACTIVE
 *
 * Multiple connections can be polled concurrently - each call to the returned function
 * creates an independent polling loop.
 *
 * Supports cancellation via AbortSignal - useful for cleanup when components unmount.
 *
 * @param options - Optional configuration for the hook
 * @returns Function that takes a connectionId and optional AbortSignal,
 * returns a promise resolving to the connection result
 *
 * @example
 * const waitForConnectionToBeReady = useWaitForConnectionToBeReady();
 *
 * // Wait for a single connection:
 * const result = await waitForConnectionToBeReady(connectionId);
 *
 * // Wait for multiple connections concurrently:
 * const [result1, result2] = await Promise.all([
 *   waitForConnectionToBeReady(connectionId1),
 *   waitForConnectionToBeReady(connectionId2),
 * ]);
 *
 * // With cancellation support:
 * const abortController = new AbortController();
 * const result = await waitForConnectionToBeReady(connectionId, abortController.signal);
 * // To cancel: abortController.abort();
 */
export function useWaitForConnectionToBeReady(
  options?: IUseWaitForConnectionToBeReadyOptions,
): (
  connectionId: string,
  signal?: AbortSignal,
) => Promise<IWaitForConnectionResult> {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    maxRetries = DEFAULT_MAX_RETRIES,
  } = options ?? {};
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const waitForConnectionToBeReady = useCallback(
    async (
      connectionId: string,
      signal?: AbortSignal,
    ): Promise<IWaitForConnectionResult> => {
      if (!organizationId || !workspaceId) {
        throw new Error('Organization ID and Workspace ID are required');
      }

      // Poll until terminal state is reached, max retries exceeded, or cancelled
      // Each call creates its own independent polling loop
      let state: V2BookConnectionState | undefined;
      let connection: V2BookConnectionDescriptor | undefined;
      let retryCount = 0;

      while (!isTerminalState(state)) {
        // Check if cancelled
        if (signal?.aborted) {
          return {
            state: state || V2BookConnectionState.BOOK_CONNECTION_STATE_UNKNOWN,
            isReady: false,
            isFailed: false,
            isConditionsNotMet: false,
            isInactive: false,
            isTimedOut: false,
            isCancelled: true,
            connection,
          };
        }

        // Check for max retries exceeded
        if (retryCount >= maxRetries) {
          return {
            state: state || V2BookConnectionState.BOOK_CONNECTION_STATE_UNKNOWN,
            isReady: false,
            isFailed: false,
            isConditionsNotMet: false,
            isInactive: false,
            isTimedOut: true,
            isCancelled: false,
            connection,
          };
        }

        try {
          const response = await retrieveWorkspaceBookConnection(
            organizationId,
            workspaceId,
            connectionId,
            { signal }, // Pass signal to axios for network request cancellation
          );

          connection = response.data?.book_connection;
          state = connection?.status?.state;
        } catch (error) {
          // Check if the error is due to abort
          if (signal?.aborted) {
            return {
              state:
                state || V2BookConnectionState.BOOK_CONNECTION_STATE_UNKNOWN,
              isReady: false,
              isFailed: false,
              isConditionsNotMet: false,
              isInactive: false,
              isTimedOut: false,
              isCancelled: true,
              connection,
            };
          }
          // Re-throw other errors
          throw error;
        }

        retryCount++;

        if (!isTerminalState(state)) {
          // Wait before next poll (also check for abort during sleep)
          await sleep(pollingInterval);
        }
      }

      return {
        state: state as V2BookConnectionState,
        isReady: state === V2BookConnectionState.BOOK_CONNECTION_STATE_READY,
        isFailed: state === V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED,
        isConditionsNotMet:
          state ===
          V2BookConnectionState.BOOK_CONNECTION_STATE_CONDITIONS_NOT_MET,
        isInactive:
          state === V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE,
        isTimedOut: false,
        isCancelled: false,
        connection,
      };
    },
    [organizationId, workspaceId, pollingInterval, maxRetries],
  );

  return waitForConnectionToBeReady;
}
